import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, FileWarning } from "lucide-react";
import { ImportResult } from "@/schemas";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ImportRecapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: ImportResult;
  onClose: () => void;
}

export function ImportRecapModal({
  open,
  onOpenChange,
  result,
  onClose,
}: ImportRecapModalProps) {
  const skippedTransactions = result.skipped_transactions || [];
  const hasIssues =
    result.missing_assets.length > 0 ||
    skippedTransactions.length > 0 ||
    result.errors.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                hasIssues
                  ? "bg-yellow-100 dark:bg-yellow-900/30"
                  : "bg-green-100 dark:bg-green-900/30"
              }`}
            >
              {hasIssues ? (
                <FileWarning className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              )}
            </div>
            <DialogTitle className="text-xl font-semibold">
              Import Summary
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Statistics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900/50 dark:bg-green-900/20">
              <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                {result.transactions_imported}
              </div>
              <div className="text-sm text-green-600 dark:text-green-500">
                Transactions imported
              </div>
            </div>
            <div className="rounded-lg border border-muted bg-muted/30 p-4">
              <div className="text-2xl font-bold text-muted-foreground">
                {result.transactions_skipped}
              </div>
              <div className="text-sm text-muted-foreground">
                Transactions skipped
              </div>
            </div>
          </div>

          {/* Missing Assets */}
          {result.missing_assets.length > 0 && (
            <div className="space-y-2">
              <h4 className="flex items-center gap-2 font-medium text-yellow-700 dark:text-yellow-400">
                <AlertCircle className="h-4 w-4" />
                Assets Not Found ({result.missing_assets.length})
              </h4>
              <ScrollArea className="h-[150px] rounded-lg border">
                <div className="p-3 space-y-2">
                  {result.missing_assets.map((asset, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-md bg-muted/50 p-2 text-sm"
                    >
                      <span className="shrink-0 font-mono text-muted-foreground">
                        Row {asset.row_number}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">
                          {asset.identifier}
                        </div>
                        {asset.description && (
                          <div className="text-muted-foreground text-xs break-words">
                            {asset.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <p className="text-xs text-muted-foreground">
                These assets are not yet registered in the system. Please contact
                support to have them added.
              </p>
            </div>
          )}

          {/* Skipped Transactions */}
          {skippedTransactions.length > 0 && (
            <div className="space-y-2">
              <h4 className="flex items-center gap-2 font-medium text-yellow-700 dark:text-yellow-400">
                <FileWarning className="h-4 w-4" />
                Skipped Transactions ({skippedTransactions.length})
              </h4>
              <ScrollArea className="h-[120px] rounded-lg border">
                <div className="p-3 space-y-2">
                  {skippedTransactions.map((skipped, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-md bg-muted/50 p-2 text-sm"
                    >
                      <span className="shrink-0 font-mono text-muted-foreground">
                        Row {skipped.row_number}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">
                          {skipped.category} / {skipped.type}
                        </div>
                        <div className="text-muted-foreground text-xs break-words">
                          {skipped.reason}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Errors */}
          {result.errors.length > 0 && (
            <div className="space-y-2">
              <h4 className="flex items-center gap-2 font-medium text-red-700 dark:text-red-400">
                <AlertCircle className="h-4 w-4" />
                Parsing Errors ({result.errors.length})
              </h4>
              <ScrollArea className="h-[100px] rounded-lg border border-red-200 dark:border-red-900/50">
                <div className="p-3 space-y-2">
                  {result.errors.map((error, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 text-sm text-red-600 dark:text-red-400"
                    >
                      <span className="shrink-0 font-mono">
                        Row {error.row_number}:
                      </span>
                      <span className="min-w-0 flex-1 break-words">
                        {error.message}
                      </span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
